import React from "react";
import { WarningIconRed } from "assests/GmsIcons";
import TooltipOverlay from "Components/Common/components/Button/TooltipOverlay";

type StatusDisplayProps = {
    status: string;
    timestamp?: string | number | null;
    showWarning: boolean;
    warningTooltip: string;
    id: string;
};

const formatTimestampToUTC = (timestamp: string | number | null | undefined): string | null => {
    if (!timestamp || timestamp === "null") return null;

    try {
        let date: Date;

        if (typeof timestamp === "number") {
            // Handle Unix timestamp (seconds or milliseconds)
            date = timestamp > 9999999999 ? new Date(timestamp) : new Date(timestamp * 1000);
        } else {
            // Handle string timestamp
            const numericTimestamp = Number(timestamp);
            if (!isNaN(numericTimestamp)) {
                date = numericTimestamp > 9999999999
                    ? new Date(numericTimestamp)
                    : new Date(numericTimestamp * 1000);
            } else {
                date = new Date(timestamp);
            }
        }

        if (isNaN(date.getTime())) return null;

        const day = date.getUTCDate();
        const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes().toString().padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        const formattedHours = hours % 12 || 12;

        return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm} UTC`;
    } catch {
        return null;
    }
};

export const StatusDisplay = ({
    status,
    timestamp,
    showWarning,
    warningTooltip,
    id,
}: StatusDisplayProps) => {
    const getStatusStyles = (status: string, hasWarning: boolean) => {
        const normalizedStatus = status.toLowerCase();

        if (hasWarning) {
            return {
                bgColor: "tw-bg-[#FFEBE6]", // Red-Error-100
                textColor: "tw-text-[#BF2600]", // Red-Error-900
            };
        }

        switch (normalizedStatus) {
            case "generated":
                return {
                    bgColor: "tw-bg-[#FCF6E3]", // Yellow-Warning-100
                    textColor: "tw-text-[#906F00]", // Yellow-Warning-900
                };
            case "generating":
                return {
                    bgColor: "tw-bg-[#DEEBFF]", // Blue-100
                    textColor: "tw-text-[#0747A6]", // Blue-900
                };
            case "promoted":
                return {
                    bgColor: "tw-bg-[#E3FCF4]", // Green-Success-100
                    textColor: "tw-text-[#115B3C]", // Green-Success-900
                };
            case "promoting":
                return {
                    bgColor: "tw-bg-[#E4D7FF]", // Purple-Secondary-accent-100
                    textColor: "tw-text-[#3007A6]", // Purple-Secondary-accent-900
                };
            case "validating":
                return {
                    bgColor: "tw-bg-[#DEEBFF]", // Blue-100
                    textColor: "tw-text-[#0747A6]", // Blue-900
                };
            case "failed":
                return {
                    bgColor: "tw-bg-[#FFEBE6]", // Red-Error-100
                    textColor: "tw-text-[#BF2600]", // Red-Error-900
                };
            case "not generated":
            case "not promoted":
                return {
                    bgColor: "tw-bg-[#DADBDD]", // Grey-200
                    textColor: "tw-text-[#4F5766]", // Grey-700
                };
            default:
                return {
                    bgColor: "tw-bg-[#DADBDD]", // Grey-200
                    textColor: "tw-text-[#4F5766]", // Grey-700
                };
        }
    };

    const formattedTimestamp = formatTimestampToUTC(timestamp);
    const shouldShowTimestamp =
        formattedTimestamp &&
        (status.toLowerCase() === "generated" || status.toLowerCase() === "promoted" || status.toLowerCase() === "failed");

    const { bgColor, textColor } = getStatusStyles(status, showWarning);

    return (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full">
            <div
                className={`tw-px-2 tw-py-0.5 ${bgColor} tw-rounded-[3px] tw-flex tw-justify-center tw-items-center tw-gap-1 tw-min-w-[120px] tw-max-w-[140px]`}
                data-testid={`${id}-status-badge`}
            >
                <span
                    className={`tw-text-xs tw-font-medium ${textColor} tw-leading-4`}
                >
                    {status}
                </span>
                {showWarning && (
                    <TooltipOverlay
                        id={`${id}-warning-tooltip`}
                        placement="top"
                        message={warningTooltip}
                    >
                        <div
                            className="tw-cursor-help tw-flex tw-items-center tw-w-3.5 tw-h-3.5"
                            data-testid={`${id}-warning-icon`}
                        >
                            <WarningIconRed />
                        </div>
                    </TooltipOverlay>
                )}
            </div>
            {shouldShowTimestamp && (
                <div
                    className="tw-text-[#6B778C] tw-text-[10px] tw-font-medium tw-leading-4"
                    data-testid={`${id}-timestamp`}
                >
                    {formattedTimestamp}
                </div>
            )}
        </div>
    );
};